import React, { useState, useEffect } from 'react';
import { api, fileUrl } from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { categoryOptions, INVITATION_CATEGORY, joinGuests, splitGuests, VAT_EXCLUDED_CATEGORIES } from '../utils/expenseCategories';
import './submitExpense.css';

// Inline FX Override Component to avoid import issues
const FxOverrideModal = ({ 
  fetchedRate, 
  currentRate, 
  originalAmount, 
  originalCurrency, 
  reimbursementCurrency, 
  onOverrideApplied, 
  onClose 
}: {
  fetchedRate: number;
  currentRate: number;
  originalAmount: number;
  originalCurrency: string;
  reimbursementCurrency: string;
  onOverrideApplied: (newRate: number, reason: string) => void;
  onClose: () => void;
}) => {
  const [inputMode, setInputMode] = useState('amount');
  const [overrideRate, setOverrideRate] = useState(currentRate.toString());
  const [reimbursementAmount, setReimbursementAmount] = useState((originalAmount * currentRate).toFixed(2));
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  // Expected reimbursement using fetched rate (rounded to 2dp like the UI)
  const expectedAmount = Math.round(originalAmount * fetchedRate * 100) / 100;

  // Effective rate depending on input mode
  const effectiveRate = (() => {
    if (inputMode === 'amount') {
      const amt = parseFloat(reimbursementAmount);
      if (!isFinite(amt) || originalAmount <= 0) return 0;
      return amt / originalAmount;
    }
    const rate = parseFloat(overrideRate);
    return isFinite(rate) ? rate : 0;
  })();

  // Implied rate used for display
  const impliedRate = effectiveRate || 0;

  // Compare based on rounded reimbursement amounts to avoid tiny FX rounding noise
  const actualAmount = Math.round((originalAmount * effectiveRate) * 100) / 100;
  let deviationPercent = 0;
  let deltaAmount = 0;
  if (expectedAmount > 0 && effectiveRate > 0) {
    deltaAmount = actualAmount - expectedAmount;
    const absDelta = Math.abs(deltaAmount);
    // Treat < 0.005 deviations (sub‑cent) as zero so using fetched rate shows 0.00%
    if (absDelta < 0.005) {
      deltaAmount = 0;
      deviationPercent = 0;
    } else {
      deviationPercent = (absDelta / expectedAmount) * 100;
    }
  }

  const isValidOverride = effectiveRate > 0 && deviationPercent <= 5;

  // Color coding: green when override lower, orange when higher within limit, red when above limit
  const deviationColor = (() => {
    if (expectedAmount <= 0 || effectiveRate <= 0) return '#6b7280';
    if (deltaAmount < 0) return '#10b981'; // negative deviation
    if (deviationPercent > 5) return '#ef4444'; // above limit
    if (deltaAmount > 0) return '#f97316'; // positive within limit
    return '#6b7280';
  })();

  const handleApply = () => {
    if (!reason.trim()) {
      setMessage('Please provide a reason for the override');
      return;
    }
    if (!isValidOverride) {
      setMessage('Override rate exceeds the ±5% limit or is invalid');
      return;
    }
    onOverrideApplied(effectiveRate, reason);
  };

  // Update reimbursement amount when rate changes (rate mode)
  const handleRateChange = (newRate: string) => {
    setOverrideRate(newRate);
    const rate = parseFloat(newRate);
    if (!isNaN(rate)) {
      setReimbursementAmount((originalAmount * rate).toFixed(2));
    }
  };

  // Update rate when reimbursement amount changes (amount mode)
  const handleAmountChange = (newAmount: string) => {
    setReimbursementAmount(newAmount);
    const amount = parseFloat(newAmount);
    if (!isNaN(amount) && originalAmount > 0) {
      const newRate = amount / originalAmount;
      setOverrideRate(newRate.toString());
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        maxWidth: '500px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}> FX Rate Override</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Current Rate: {originalCurrency} → {reimbursementCurrency}</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>{fetchedRate.toFixed(6)}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Expected: {originalAmount} {originalCurrency} = {(originalAmount * fetchedRate).toFixed(2)} {reimbursementCurrency}
          </div>
        </div>
        
        {/* Input Mode Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Override Method</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setInputMode('amount')}
              style={{
                padding: '8px 16px',
                backgroundColor: inputMode === 'amount' ? '#3b82f6' : '#f3f4f6',
                color: inputMode === 'amount' ? 'white' : '#374151',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              By Amount Received
            </button>
            <button
              onClick={() => setInputMode('rate')}
              style={{
                padding: '8px 16px',
                backgroundColor: inputMode === 'rate' ? '#3b82f6' : '#f3f4f6',
                color: inputMode === 'rate' ? 'white' : '#374151',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              By Exchange Rate
            </button>
          </div>
        </div>
        
        {inputMode === 'amount' ? (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Amount Actually Paid ({reimbursementCurrency}) *
            </label>
            <input
              type="number"
              step="0.01"
              value={reimbursementAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              style={{
                width: '100%', padding: '10px', border: `1px solid ${isValidOverride ? '#d1d5db' : '#ef4444'}`,
                borderRadius: '6px', fontSize: '14px'
              }}
              placeholder={`Enter amount in ${reimbursementCurrency}`}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Implied Rate: {impliedRate.toFixed(6)} {originalCurrency}/{reimbursementCurrency}
            </div>
            <div style={{ fontSize: '12px', color: deviationColor, marginTop: '2px' }}>
              Change: {deviationPercent === 0 || deltaAmount === 0
                ? '0.00%'
                : `${deltaAmount < 0 ? '-' : '+'}${deviationPercent.toFixed(2)}%`}{' '}
              {isValidOverride ? '(Valid)' : '(Exceeds 5% limit)'}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>New Exchange Rate *</label>
            <input
              type="number"
              step="0.000001"
              value={overrideRate}
              onChange={(e) => handleRateChange(e.target.value)}
              style={{
                width: '100%', padding: '10px', border: `1px solid ${isValidOverride ? '#d1d5db' : '#ef4444'}`,
                borderRadius: '6px', fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Reimbursement: {reimbursementAmount} {reimbursementCurrency}
            </div>
            <div style={{ fontSize: '12px', color: deviationColor, marginTop: '2px' }}>
              Change: {deviationPercent === 0 || deltaAmount === 0
                ? '0.00%'
                : `${deltaAmount < 0 ? '-' : '+'}${deviationPercent.toFixed(2)}%`}{' '}
              {isValidOverride ? '(Valid)' : '(Exceeds 5% limit)'}
            </div>
          </div>
        )}
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Reason *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you're overriding the exchange rate..."
            style={{
              width: '100%', padding: '10px', border: '1px solid #d1d5db',
              borderRadius: '6px', fontSize: '14px', minHeight: '60px', resize: 'vertical'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!isValidOverride || !reason.trim()}
            style={{
              padding: '10px 16px',
              backgroundColor: (!isValidOverride || !reason.trim()) ? '#d1d5db' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: (!isValidOverride || !reason.trim()) ? 'not-allowed' : 'pointer'
            }}
          >
            Apply Override
          </button>
        </div>
        
        {message && (
          <div style={{
            marginTop: '12px', padding: '10px', borderRadius: '6px',
            backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '14px'
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

const SubmitExpense: React.FC = () => {
  const [date, setDate] = useState('');
  const [type, setType] = useState('');
  const [justification, setJustification] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [reimbursementAmount, setReimbursementAmount] = useState<number | null>(null);
  const [userReimbursementCurrency, setUserReimbursementCurrency] = useState<string>('EUR');
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [existingReceiptIsImage, setExistingReceiptIsImage] = useState<boolean>(false);
  
  // FX Override states
  const [showFxOverride, setShowFxOverride] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [fetchedRate, setFetchedRate] = useState<number | null>(null);
  const [isRateOverridden, setIsRateOverridden] = useState(false);
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [hasStoredFxData, setHasStoredFxData] = useState(false);
  const [noReceiptDeclaration, setNoReceiptDeclaration] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<any>(null);
  const [dateError, setDateError] = useState<string>('');
  const [classification, setClassification] = useState<string>('');
  const [vatAmount, setVatAmount] = useState<string>('');
  const [showVatField, setShowVatField] = useState<boolean>(false);
  const [guestNames, setGuestNames] = useState<string>('');
  const [showCustomCurrency, setShowCustomCurrency] = useState<boolean>(false);
  const [customCurrency, setCustomCurrency] = useState<string>('');
  const [currencyError, setCurrencyError] = useState<string>('');
  const [fileSizeWarning, setFileSizeWarning] = useState<string>('');
  
  // File size limit: 10MB
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  
  // Validate file size
  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setFileSizeWarning(`⚠️ File size (${fileSizeMB}MB) exceeds the 10MB limit. Please compress or choose a smaller file.`);
      return false;
    }
    setFileSizeWarning('');
    return true;
  };
  
  // Validate currency code format (3 uppercase letters)
  const validateCurrency = (code: string): boolean => {
    return /^[A-Z]{3}$/.test(code);
  };
  
  // Handle currency selection change
  const markFxDataDirty = () => {
    setHasStoredFxData(false);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    setCurrency(selected);
    setShowCustomCurrency(selected === 'OTHER');
    if (selected !== 'OTHER') {
      setCustomCurrency('');
      setCurrencyError('');
    }
    markFxDataDirty();
  };

  // Handle custom currency input change
  const handleCustomCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setCustomCurrency(value);
    if (value && !validateCurrency(value)) {
      setCurrencyError('Please enter a valid 3-letter currency code (e.g., USD, EUR)');
    } else {
      setCurrencyError('');
    }
    markFxDataDirty();
  };

  // Get the final currency value (selected or custom)
  const getFinalCurrency = (): string => {
    return currency === 'OTHER' ? customCurrency : currency;
  };

  // Determine if VAT field should be shown
  useEffect(() => {
    const isVatApplicable = 
      (classification === 'support' || classification === 'mix') && 
      currency === 'EUR' && 
      !VAT_EXCLUDED_CATEGORIES.includes(type);
    
    setShowVatField(isVatApplicable);
    if (!isVatApplicable) {
      setVatAmount('');
    }
  }, [classification, currency, type]);

  const location = useLocation();
  const navigate = useNavigate();
  
  // Handle token from URL (iOS Shortcut)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      console.log('📱 SubmitExpense: Saving token from URL');
      localStorage.setItem('token', urlToken);
      setToken(urlToken);
    }
  }, [location]);
  
  // Fetch user profile
  useEffect(() => {
    // Check if token is in URL (iOS Shortcut case)
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    
    // If no token in state and no token in URL, redirect to login
    if (!token && !urlToken) {
      navigate('/login');
      return;
    }
    
    // Use token from state if available, otherwise wait for it to be set
    if (!token) {
      return;
    }
    
    const fetchProfile = async () => {
      try {
        const response = await api.get('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        navigate('/login');
      }
    };
    
    fetchProfile();
  }, [token, location, navigate]);

  // Check if we're in edit mode
  const editingExpense = location.state?.editingExpense;
  const isEditMode = !!editingExpense;

  // Debug: Log edit mode detection
  console.log(' Edit mode detection:', { 
    locationState: location.state, 
    editingExpense, 
    isEditMode,
    expenseId: editingExpense?.expense_id 
  });

  // Expense types and currencies (user-specified requirements)
  const currencies = ['EUR', 'USD', 'KES', 'NGN', 'ZAR', 'XOF', 'XAF', 'GHS', 'CHF', 'GBP', 'MAD', 'EGP', 'OTHER'];
  const currencyNames: {[key: string]: string} = {
    'EUR': 'Euro',
    'USD': 'US Dollar',
    'KES': 'Kenyan Shilling',
    'NGN': 'Nigerian Naira',
    'ZAR': 'South African Rand',
    'XOF': 'West African CFA Franc',
    'XAF': 'Central African CFA Franc',
    'GHS': 'Ghanaian Cedi',
    'CHF': 'Swiss Franc',
    'GBP': 'British Pound',
    'MAD': 'Moroccan Dirham',
    'EGP': 'Egyptian Pound',
    'OTHER': 'Other (specify)'
  };

  // Get user's reimbursement currency and role on component mount and populate form if editing
  React.useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;
      try {
        // Get user role from JWT token
        const payload = JSON.parse(atob(token.split('.')[1]));
        const detectedRole = payload.role || '';
        setUserRole(detectedRole);
        console.log(' User role detected from JWT:', detectedRole);
        console.log(' Full JWT payload:', payload);

        // Get user reimbursement currency
        const response = await api.get('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.reimbursement_currency) {
          setUserReimbursementCurrency(response.data.reimbursement_currency);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };
    fetchUserData();

    // Initialize stored FX data flag based on edit mode
    if (!isEditMode) {
      setHasStoredFxData(false);
      console.log(' New expense - allowing FX rate fetching');
    }

    // Pre-populate form if in edit mode
    if (isEditMode && editingExpense) {
      console.log('Pre-populating form with expense data:', editingExpense);
      setDate(editingExpense.date || '');
      setType(editingExpense.type || '');
      if (editingExpense.type === INVITATION_CATEGORY) {
        const { description, guests } = splitGuests(editingExpense.justification || '');
        setJustification(description);
        setGuestNames(guests);
      } else {
        setJustification(editingExpense.justification || '');
      }
      setAmount(editingExpense.amount?.toString() || '');

      // Currency handling: if stored currency isn't in our predefined list, switch to OTHER and set custom code
      const storedCurrency = editingExpense.currency || 'EUR';
      const predefined = ['EUR','USD','KES','NGN','ZAR','XOF','XAF','GHS','CHF','GBP','MAD','EGP','OTHER'];
      if (!predefined.includes(storedCurrency)) {
        setCurrency('OTHER');
        setCustomCurrency(storedCurrency);
        setShowCustomCurrency(true);
      } else {
        setCurrency(storedCurrency);
        if (storedCurrency === 'OTHER' && editingExpense.currency_other) {
          setCustomCurrency(editingExpense.currency_other);
          setShowCustomCurrency(true);
        } else {
          setShowCustomCurrency(false);
        }
      }

      // Normalize classification to internal values used by UI: 'support' | 'investment' | 'mix'
      if (editingExpense.classification) {
        const rawClass = String(editingExpense.classification).toLowerCase();
        let normalizedClass = '';
        // Any combination that includes both support and (core/investment/mix) is a mix
        const hasSupport = rawClass.includes('support');
        const hasCoreOrInvestment = rawClass.includes('core') || rawClass.includes('investment');
        const hasMix = rawClass.includes('mix');
        if ((hasSupport && hasCoreOrInvestment) || hasMix) {
          normalizedClass = 'mix';
        } else if (hasSupport) {
          normalizedClass = 'support';
        } else if (hasCoreOrInvestment) {
          normalizedClass = 'investment';
        } else {
          // Fallback: if we get an unknown value, keep as-is if it matches our UI values
          if (['support', 'investment', 'mix'].includes(rawClass)) {
            normalizedClass = rawClass as 'support' | 'investment' | 'mix';
          } else {
            normalizedClass = '';
          }
        }
        if (normalizedClass) setClassification(normalizedClass);
      }

      // VAT and declaration
      const existingVat = editingExpense.vat_amount !== undefined && editingExpense.vat_amount !== null
        ? String(editingExpense.vat_amount)
        : '';
      setVatAmount(existingVat);
      if (existingVat !== '') {
        setShowVatField(true);
      }
      setNoReceiptDeclaration(!!editingExpense.no_receipt);

      // Pre-populate FX override data to preserve stored rates
      if (editingExpense.exchange_rate) {
        setExchangeRate(editingExpense.exchange_rate);
        setHasStoredFxData(true); // Mark that we have stored FX data
        console.log(' Pre-populated exchange rate from stored expense:', editingExpense.exchange_rate);
        console.log(' Pre-populated exchange rate from stored expense:', editingExpense.exchange_rate);
      }
      if (editingExpense.reimbursement_amount) {
        setReimbursementAmount(editingExpense.reimbursement_amount);
        console.log(' Pre-populated reimbursement amount from stored expense:', editingExpense.reimbursement_amount);
      }
      if (editingExpense.fetched_exchange_rate) {
        setFetchedRate(editingExpense.fetched_exchange_rate);
        console.log(' Pre-populated fetched rate from stored expense:', editingExpense.fetched_exchange_rate);
      }
      if (editingExpense.override_exchange_rate) {
        setIsRateOverridden(true);
        console.log(' Expense has FX override - preserving override state');
      }

      // Note: We don't pre-populate receipt file, but show existing receipt info
      if (editingExpense.receipt) {
        const cacheBusted = `${fileUrl(`/uploads/${editingExpense.receipt}`)}?v=${editingExpense.updated_at || editingExpense.submitted_at || Date.now()}`;
        const previewUrl = cacheBusted;
        setReceiptPreview(previewUrl);
        const ext = editingExpense.receipt.split('.').pop()?.toLowerCase() || '';
        const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif'];
        setExistingReceiptIsImage(imageExts.includes(ext));
      } else {
        setExistingReceiptIsImage(false);
      }
    }
  }, [token, isEditMode, editingExpense]);

  // Handle files from iOS Shortcut (via URL parameter)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uploadedFilename = params.get('uploaded');
    
    console.log('📱 File fetch effect running:', { uploadedFilename, hasToken: !!token });

    if (uploadedFilename && token) {
      console.log('📱 Handling uploaded file from iOS Shortcut:', uploadedFilename);
      setMessage('⏳ Loading receipt from shortcut...');
      
      // Fetch the file from the server to create a preview
      // Use API subdomain which has CORS headers, not main domain served by nginx
      const apiBase = process.env.REACT_APP_API_BASE || 'https://api.staging.expenses.firstgrowthventures.com';
      const fileUrl = `${apiBase}/uploads/${uploadedFilename}`;
      console.log('📱 Fetching file from:', fileUrl);
      console.log('📱 API Base:', apiBase);
      
      // Don't send Authorization header to avoid CORS preflight
      fetch(fileUrl)
        .then(response => {
          console.log('📱 Fetch response:', response.status, response.statusText);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.blob();
        })
        .then(blob => {
          console.log('📱 Blob received:', blob.size, 'bytes, type:', blob.type);
          // Create a File object from the blob
          const file = new File([blob], uploadedFilename, { type: blob.type });
          console.log('📱 File object created:', file.name, file.size, file.type);
          setReceipt(file);
          
          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            console.log('📱 FileReader completed, preview length:', (reader.result as string)?.length);
            setReceiptPreview(reader.result as string);
            const ext = uploadedFilename.split('.').pop()?.toLowerCase() || '';
            const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif'];
            const isImage = imageExts.includes(ext);
            console.log('📱 File extension:', ext, 'isImage:', isImage);
            setExistingReceiptIsImage(isImage);
          };
          reader.onerror = (error) => {
            console.error('📱 FileReader error:', error);
          };
          reader.readAsDataURL(file);
          
          setMessage('✅ Receipt attached from iOS Shortcut!');
          console.log('✅ Successfully attached receipt from shortcut');
          
          // Clean up URL - remove both token and uploaded params
          setTimeout(() => {
            window.history.replaceState({}, '', '/submit-expense');
          }, 100);
        })
        .catch(error => {
          console.error('❌ Error fetching uploaded file:', error);
          setMessage(`⚠️ Failed to load uploaded file: ${error.message}. Please upload manually.`);
          // Clean up URL even on error
          setTimeout(() => {
            window.history.replaceState({}, '', '/submit-expense');
          }, 100);
        });
    }
  }, [token]);

  // Fetch exchange rate when amount, currency, or date changes
  React.useEffect(() => {
    const fetchExchangeRate = async () => {
      const finalCurrency = getFinalCurrency();
      
      if (!amount || !finalCurrency || !date) {
        setExchangeRate(null);
        setReimbursementAmount(null);
        return;
      }

      // Don't fetch rates for invalid dates
      const dateValidationError = validateDate(date);
      if (dateValidationError) {
        setExchangeRate(null);
        setReimbursementAmount(null);
        console.log(' Skipping FX rate fetch - invalid date:', dateValidationError);
        return;
      }

      // Handle same currency conversion
      if (finalCurrency === userReimbursementCurrency) {
        setExchangeRate(1.0);
        setReimbursementAmount(parseFloat(amount));
        setFetchedRate(1.0);
        setIsRateOverridden(false);
        setHasStoredFxData(false);
        console.log(' Same currency conversion:', finalCurrency, '-> rate: 1.0');
        return;
      }

      // Skip fetching if we have stored FX data from the database
      if (hasStoredFxData) {
        console.log(' Skipping FX rate fetch - using stored expense data from database');
        return;
      }

      setIsLoadingRate(true);
      try {
        console.log('Fetching exchange rate for:', {
          from: finalCurrency,
          to: userReimbursementCurrency,
          date: date
        });
        
        const response = await api.get(
          `/api/exchange-rate?from=${finalCurrency}&to=${userReimbursementCurrency}&date=${date}`
        );
        
        const rate = response.data.rate;
        console.log('Received exchange rate:', rate);
        
        setFetchedRate(rate); // Store the original fetched rate
        setExchangeRate(rate); // This will be the rate used (fetched or overridden)
        setIsRateOverridden(false); // Reset override status when fetching new rate
        setHasStoredFxData(false); // Reset stored FX flag when fetching new rate
        
        const calculatedAmount = Math.round((parseFloat(amount) * rate) * 100) / 100;
        setReimbursementAmount(calculatedAmount);
        console.log('Calculated reimbursement:', calculatedAmount);
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        setExchangeRate(null);
        setReimbursementAmount(null);
      } finally {
        setIsLoadingRate(false);
      }
    };

    const timeoutId = setTimeout(fetchExchangeRate, 500);
    return () => clearTimeout(timeoutId);
  }, [amount, currency, customCurrency, date, userReimbursementCurrency, hasStoredFxData]);

  // Expense types and currencies are now defined at the top of the component

  const today = new Date().toISOString().split('T')[0];
  // Calculate 12 months ago for date validation
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  const minDate = twelveMonthsAgo.toISOString().split('T')[0];

  // Date validation function
  const validateDate = (dateValue: string): string => {
    if (!dateValue) return '';

    const selectedDate = new Date(dateValue);
    const todayDate = new Date(today);
    const minDateObj = new Date(minDate);

    if (selectedDate > todayDate) {
      return 'Future dates are not allowed. Please select today or an earlier date.';
    }

    if (selectedDate < minDateObj) {
      return 'Dates older than 12 months are not allowed. Please select a more recent date.';
    }

    return ''; // Valid date
  };

  // Check if date is valid for form submission
  const isDateValid = dateError === '' && date !== '';

  // Check if all mandatory fields are filled (including receipt or no-receipt declaration)
  const hasReceipt = !!(receipt || (isEditMode && editingExpense?.receipt) || noReceiptDeclaration);
  const canDeclareNoReceipt = (userRole === 'admin' || userRole === 'partner' || userRole === 'super_admin') && noReceiptDeclaration;
  const hasReceiptOrDeclaration = hasReceipt || canDeclareNoReceipt;

  // Check if VAT amount is required and valid
  // VAT is optional if 'no receipt' is checked, otherwise it's required when showVatField is true
  const isVatAmountValid = !showVatField || noReceiptDeclaration || (vatAmount && !isNaN(parseFloat(vatAmount)) && parseFloat(vatAmount) >= 0);

  const isInvitation = type === INVITATION_CATEGORY;
  const hasRequiredFields = type && justification && amount && currency && classification && isVatAmountValid && (!isInvitation || guestNames.trim());
  const isFormComplete = isDateValid && hasRequiredFields && hasReceiptOrDeclaration;

  // Allow submission if form is complete OR if we're editing an existing expense (including drafts) - but always require valid date and classification
  const canSubmit = (isFormComplete || (isEditMode && isDateValid && hasRequiredFields && hasReceiptOrDeclaration)) && isDateValid;

  // Debug: Log validation state
  console.log('Validation check:', { 
    date, type, justification, amount, currency, 
    receipt: !!receipt, 
    existingReceipt: editingExpense?.receipt,
    hasReceipt,
    userRole,
    noReceiptDeclaration,
    canDeclareNoReceipt,
    hasReceiptOrDeclaration,
    isFormComplete, 
    canSubmit 
  });

  const handleSubmit = async (isDraft = false) => {
    console.log(' handleSubmit called with isDraft:', isDraft);
    console.log(' Current form state:', { date, type, justification, amount, currency, receipt: !!receipt });

    setIsSubmitting(true);
    setMessage('');

    // Date validation - prevent submission with invalid dates (but allow drafts without date)
    if (!isDraft && (!isDateValid || dateError)) {
      setMessage(` Cannot submit expense with invalid date. ${dateError || 'Please select a valid date.'}`);
      setIsSubmitting(false);
      return;
    }

    // Basic validation - allow draft without receipt, or no-receipt declaration for partners/super admins
    const hasReceiptForSubmission = receipt || (isEditMode && editingExpense?.receipt);
    const canSubmitWithoutReceipt = (userRole === 'admin' || userRole === 'partner' || userRole === 'super_admin') && noReceiptDeclaration;

    if (!isDraft && !hasReceiptForSubmission && !canSubmitWithoutReceipt) {
      if (userRole === 'admin' || userRole === 'partner' || userRole === 'super_admin') {
        setMessage(' Receipt is required for submission. You can either upload a receipt or declare on your honor that you don\'t have one.');
      } else {
        setMessage(' Receipt is required for submission. Use "Save as Draft" to save without receipt.');
      }
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('Form data:', { date, type, justification, amount, currency, isDraft, receipt: receipt?.name });
      console.log('Receipt validation:', { 
        receipt: !!receipt, 
        existingReceipt: editingExpense?.receipt, 
        hasReceiptForSubmission, 
        isEditMode, 
        isDraft 
      });

      const formData = new FormData();
      formData.append('date', date);
      formData.append('type', type);
      formData.append('justification', isInvitation ? joinGuests(justification, guestNames) : justification);
      formData.append('amount', amount);
      formData.append('currency', getFinalCurrency());
      formData.append('status', isDraft ? 'draft' : 'submitted');

      // Add classification if provided
      if (classification) {
        formData.append('classification', classification);
      }

      // Add VAT amount if provided (do not gate solely by UI visibility)
      if (vatAmount !== '') {
        formData.append('vat_amount', vatAmount);
      }

      // Persist no-receipt declaration state to backend
      formData.append('no_receipt', noReceiptDeclaration ? 'true' : 'false');

      // Add exchange rate and reimbursement data
      console.log(' DEBUG: Form submission state:', {
        exchangeRate,
        reimbursementAmount,
        fetchedRate,
        isRateOverridden,
        amount
      });

      if (exchangeRate) {
        formData.append('exchange_rate', exchangeRate.toString());
        console.log(' Added exchange_rate to form:', exchangeRate.toString());
      }
      if (reimbursementAmount) {
        formData.append('reimbursement_amount', reimbursementAmount.toString());
        console.log(' Added reimbursement_amount to form:', reimbursementAmount.toString());
      }

      // Add FX override tracking data
      if (fetchedRate) {
        formData.append('fetched_exchange_rate', fetchedRate.toString());
        console.log(' Added fetched_exchange_rate to form:', fetchedRate.toString());
      }
      if (exchangeRate && isRateOverridden) {
        formData.append('override_exchange_rate', exchangeRate.toString());
        formData.append('override_reason', overrideReason);
        console.log(' Added override data to form:', { override_exchange_rate: exchangeRate.toString(), override_reason: overrideReason });
      }

      if (receipt) {
        formData.append('receipt', receipt);
      }

      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }

      let response;
      if (isEditMode && editingExpense && editingExpense.expense_id) {
        // Update existing expense
        console.log(' Updating existing expense:', editingExpense.expense_id);
        response = await api.put(`/api/expenses/${editingExpense.expense_id}`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response?.data?.expense) {
          const updatedExpense = response.data.expense;

          if (editingExpense) {
            if (updatedExpense.receipt) {
              editingExpense.receipt = updatedExpense.receipt;
            }
            if (updatedExpense.updated_at) {
              editingExpense.updated_at = updatedExpense.updated_at;
            }
          }

          if (updatedExpense.receipt) {
            const cacheBustKey = updatedExpense.updated_at || updatedExpense.submitted_at || Date.now();
            const cacheBustedUrl = `${fileUrl(`/uploads/${updatedExpense.receipt}`)}?v=${cacheBustKey}`;
            const ext = updatedExpense.receipt.split('.').pop()?.toLowerCase() || '';
            const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif'];
            setReceiptPreview(cacheBustedUrl);
            setExistingReceiptIsImage(imageExts.includes(ext));
          } else {
            setReceiptPreview(null);
            setExistingReceiptIsImage(false);
          }
        }
      } else {
        try {
          response = await api.post('/api/expenses', formData, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          console.log(' POST request successful:', response.status, response.data);
        } catch (axiosError: any) {
          console.error(' AXIOS ERROR:', axiosError);
          console.error(' AXIOS ERROR MESSAGE:', axiosError.message);
          console.error(' AXIOS ERROR RESPONSE:', axiosError.response);
          throw axiosError; // Re-throw to be caught by outer try-catch
        }
      }

      if (response.status === 201 || response.status === 200) {
        if (response.data) {
          let successMessage = ` Expense ${isDraft ? 'saved as draft' : 'submitted'} successfully!`;

          // Handle duplicate warning from backend
          if (response.data.duplicate_warning) {
            setDuplicateWarning(response.data.duplicate_warning);
            successMessage += ` Warning: ${response.data.duplicate_warning.message}`;
          }

          setMessage(successMessage);

          // Reset form after successful submission (not draft)
          if (!isDraft) {
            setDate('');
            setType('');
            setJustification('');
            setGuestNames('');
            setAmount('');
            setCurrency('');
            setReceipt(null);
            setReceiptPreview(null);
            setExchangeRate(null);
            setReimbursementAmount(null);
            setNoReceiptDeclaration(false);
          }

          // Navigate back to My Expenses after a short delay
          setTimeout(() => {
            navigate('/my-expenses');
          }, 2000);
        }
      }
    } catch (error: any) {
      console.error(' FULL ERROR DETAILS:', error);
      console.error(' Error message:', error.message);
      console.error(' Error response:', error.response);
      console.error(' Error status:', error.response?.status);
      console.error(' Error data:', error.response?.data);

      const actionText = isEditMode ? 'updating' : (isDraft ? 'saving draft' : 'submitting');
      let errorMessage = ` Error ${actionText}. Please try again.`;

      // Show more specific error if available
      if (error.response?.data?.message) {
        errorMessage = ` Error ${actionText}: ${error.response.data.message}`;
      } else if (error.response?.status === 401) {
        errorMessage = ` Authentication failed. Please log in again.`;
      } else if (error.response?.status === 400) {
        errorMessage = ` Invalid data: ${error.response?.data?.message || 'Please check all fields'}`;
      } else if (error.response?.status === 500) {
        errorMessage = ` Server error: ${error.response?.data?.message || 'Please try again later'}`;
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = ` Network error. Check if backend server is running.`;
      }

      setMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle FX override applied callback
  const handleFxOverrideApplied = (newRate: number, reason: string) => {
    console.log('FX override applied:', { newRate, reason, fetchedRate });
    setExchangeRate(newRate);
    setIsRateOverridden(true);
    setOverrideReason(reason); // Store the actual user-provided reason
    setShowFxOverride(false);

    // Recalculate reimbursement amount with new rate
    if (amount) {
      setReimbursementAmount(Math.round((parseFloat(amount) * newRate) * 100) / 100);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };
  
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        userProfile={userProfile}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div style={{ flex: 1, minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* Mobile Menu Button */}
        <div style={{ padding: '16px', display: 'block' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              marginBottom: '8px'
            }}
            aria-label="Toggle navigation"
          >
            ☰
          </button>
        </div>
        
        {/* Header */}
        <div style={{ padding: '0 16px 16px 16px', marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
            {isEditMode ? 'Edit Expense' : 'Submit Expense'}
          </h1>
          <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
            {isEditMode ? 'Update your expense details and resubmit for approval' : 'Create a new expense report for approval'}
          </p>
        </div>

      <div style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Duplicate Warning Display */}
        {duplicateWarning && (
          <div style={{
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            border: '1px solid #fb923c',
            borderRadius: '12px',
            padding: '18px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            boxShadow: '0 8px 16px rgba(249, 115, 22, 0.18)'
          }}>
            <div style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '9999px',
              backgroundColor: '#f97316',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px',
              fontWeight: 700,
              boxShadow: '0 4px 10px rgba(249, 115, 22, 0.35)'
            }}>!</div>
            <div style={{ lineHeight: 1.4 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#9a3412', marginBottom: '6px' }}>
                Possible Duplicate Expense Detected
              </div>
              <div style={{ fontSize: '13px', color: '#7c2d12' }}>
                Similar expense found: <strong style={{ color: '#9a3412' }}>{duplicateWarning.expense_id}</strong><br />
                Description: <span style={{ fontStyle: 'italic' }}>&ldquo;{duplicateWarning.justification}&rdquo;</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Expense Details Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📋 Expense Details
          </h2>
          
          <div className="submit-expense-grid">
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Date of Expense <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={e => {
                  const newDate = e.target.value;
                  setDate(newDate);
                  const error = validateDate(newDate);
                  setDateError(error);
                  markFxDataDirty();
                }}
                min={minDate}
                max={today}
                style={{ 
                  width: '100%', 
                  padding: '12px 14px', 
                  border: `2px solid ${dateError ? '#dc3545' : '#d1d5db'}`, 
                  borderRadius: '8px', 
                  fontSize: '16px',
                  fontWeight: '500',
                  boxSizing: 'border-box',
                  minHeight: '48px',
                  backgroundColor: dateError ? '#fef2f2' : '#ffffff',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  if (!dateError) {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!dateError) {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }
                }}
                required
              />
              {dateError && (
                <div style={{ 
                  color: '#dc3545', 
                  fontSize: '13px', 
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>⚠️</span>
                  {dateError}
                </div>
              )}
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Category <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '14px 16px', 
                  border: '2px solid #d1d5db', 
                  borderRadius: '8px', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  height: '56px',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '12px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              >
                <option value="">Select category...</option>
                {categoryOptions(type).map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '8px',
              color: '#374151'
            }}>
              Justification <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <textarea
              value={justification}
              onChange={e => setJustification(e.target.value)}
              placeholder="Describe the business purpose of this expense..."
              style={{ 
                width: '100%', 
                padding: '14px 16px', 
                border: '2px solid #d1d5db', 
                borderRadius: '8px', 
                fontSize: '16px', 
                fontWeight: '500',
                minHeight: '120px', 
                resize: 'vertical',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
              required
            />
            <small style={{ 
              color: '#6b7280', 
              fontSize: '12px',
              display: 'block',
              marginTop: '4px'
            }}>
              Exchange rate will be fetched for the selected date (or next business day if weekend/holiday)
            </small>
          </div>

          {isInvitation && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                Guest name(s) <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={guestNames}
                onChange={e => setGuestNames(e.target.value)}
                placeholder="e.g. Jane Doe (Acme), John Smith (Acme)"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  outline: 'none'
                }}
                required
              />
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Guest names will be added to the description
              </small>
            </div>
          )}
        </div>

        {/* Amount & Currency Card */}
        <div style={{ 
          backgroundColor: '#f8fafc', 
          borderRadius: '12px', 
          padding: '24px', 
          marginBottom: '20px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '2px solid #e2e8f0'
        }}>
          <h2 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#1f2937'
          }}>
            💳 Amount & Currency
          </h2>
          <div className="submit-expense-exchange-grid">
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Amount <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={e => {
                  setAmount(e.target.value);
                  markFxDataDirty();
                }}
                placeholder="0.00"
                style={{ 
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxSizing: 'border-box',
                  height: '56px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#ffffff',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Currency <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                value={currency}
                onChange={handleCurrencyChange}
                style={{ 
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  height: '56px',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '12px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              >
                <option value="">Select currency...</option>
                {currencies.map(curr => (
                  <option key={curr} value={curr}>{curr === 'OTHER' ? 'Other (specify)' : `${curr} - ${currencyNames[curr]}`}</option>
                ))}
              </select>
              
              {/* Custom Currency Input */}
              {showCustomCurrency && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <input
                      type="text"
                      value={customCurrency}
                      onChange={handleCustomCurrencyChange}
                      placeholder="Enter 3-letter currency code (e.g., JPY, CAD)"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: `2px solid ${currencyError ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        height: '56px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}
                      maxLength={3}
                      onKeyPress={(e) => {
                        // Allow only letters
                        const charCode = e.which || e.keyCode;
                        const charStr = String.fromCharCode(charCode);
                        if (!/^[A-Za-z]$/.test(charStr)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        // Convert to uppercase as user types
                        const target = e.target as HTMLInputElement;
                        target.value = target.value.toUpperCase();
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = currencyError ? '#ef4444' : '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {currencyError && (
                      <div style={{ 
                        color: '#ef4444', 
                        fontSize: '12px', 
                        marginTop: '4px',
                        fontWeight: '500'
                      }}>
                        {currencyError}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Exchange Rate Display */}
          {amount && currency && date && currency !== userReimbursementCurrency && (
            <div style={{ backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '6px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span>ℹ️</span>
                <strong style={{ color: '#1976d2' }}>{currency} Conversion Required * (Your Reimbursement Currency)</strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{currency} Amount ($)</div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>{amount || '0.00'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Exchange Rate {isRateOverridden && <span style={{ color: '#f59e0b', fontWeight: '600' }}>(OVERRIDDEN)</span>}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: isRateOverridden ? '#f59e0b' : 'inherit' }}>
                    {isLoadingRate ? '...' : exchangeRate ? exchangeRate.toFixed(4) : '0.0000'}
                  </div>
                  {isRateOverridden && fetchedRate && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      Original: {fetchedRate.toFixed(4)}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {isRateOverridden ? 'Rate manually overridden' : 'Calculated automatically'}
                </div>
                {/* FX Override button for partners and super admins */}
                {(() => {
                  const canOverride = (userRole === 'admin' || userRole === 'partner' || userRole === 'super_admin') && exchangeRate && fetchedRate;
                  console.log(' FX Override check:', { userRole, canOverride, exchangeRate: !!exchangeRate, fetchedRate: !!fetchedRate });
                  return canOverride;
                })() && (
                  <button
                    onClick={() => setShowFxOverride(true)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: isRateOverridden ? '#f59e0b' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    💱 {isRateOverridden ? 'Modify Override' : 'Override Rate'}
                  </button>
                )}
              </div>
              {reimbursementAmount && (
                <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                  <strong>Reimbursement: {reimbursementAmount.toFixed(2)} {userReimbursementCurrency}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Classification */}
        <div className="submit-expense-classification-card">
          <label className="submit-expense-classification-card__label" htmlFor="classification">
            Classification <span>*</span>
          </label>
          <select
            id="classification"
            className="submit-expense-classification-card__select"
            value={classification}
            onChange={(event) => setClassification(event.target.value)}
          >
            <option value="">Select classification</option>
            <option value="support">Support</option>
            <option value="investment">Investment</option>
            <option value="mix">Mix / Unknown</option>
          </select>

          {/* VAT Amount Field - Only shown for certain conditions */}
          {showVatField && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  marginBottom: '6px',
                  color: '#374151'
                }}>
                  VAT Amount (EUR) <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={noReceiptDeclaration}
                    style={{
                      width: '150px',
                      padding: '10px 12px',
                      border: `2px solid ${!isVatAmountValid && vatAmount !== '' ? '#dc3545' : '#d1d5db'}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      backgroundColor: noReceiptDeclaration ? '#f3f4f6' : 'white',
                      cursor: noReceiptDeclaration ? 'not-allowed' : 'text',
                      opacity: noReceiptDeclaration ? 0.6 : 1
                    }}
                    onFocus={(e) => {
                      if (!noReceiptDeclaration) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = !isVatAmountValid && vatAmount !== '' ? '#dc3545' : '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                    required={showVatField && !noReceiptDeclaration}
                  />
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    (included in total amount)
                    {noReceiptDeclaration && (
                      <span style={{ display: 'block', color: '#dc2626', marginTop: '4px' }}>
                        VAT cannot be claimed without a receipt
                      </span>
                    )}
                  </span>
                </div>
                {!isVatAmountValid && vatAmount !== '' && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    Please enter a valid VAT amount (must be a positive number)
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Receipt Upload Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🧾 Receipt Upload
          </h2>
          
          {/* Receipt Upload Area - Show different UI based on whether receipt is uploaded */}
          {!receiptPreview ? (
            /* Upload Area - No Receipt */
            <div style={{ border: '2px dashed #ddd', borderRadius: '8px', padding: '40px', textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>Upload Receipt</h3>
              <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>Attach a photo or PDF of your receipt</p>
              
              <div className="submit-expense-upload-buttons">
                <label className="submit-expense-upload-buttons__item" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                  📷 Take Photo
                  <input
                    id="receipt"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      if (file && !validateFileSize(file)) {
                        e.target.value = ''; // Reset input
                        return;
                      }
                      setReceipt(file);
                      setExistingReceiptIsImage(!!file && file.type.startsWith('image/'));
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setReceiptPreview(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setReceiptPreview(null);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                <label className="submit-expense-upload-buttons__item" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                  📤 Upload File
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      if (file && !validateFileSize(file)) {
                        e.target.value = ''; // Reset input
                        return;
                      }
                      setReceipt(file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setReceiptPreview(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setReceiptPreview(null);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              
              {/* File Size Warning */}
              {fileSizeWarning && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {fileSizeWarning}
                </div>
              )}
            </div>
          ) : (
            /* Receipt Uploaded - Show Preview with Replace Option */
            <div style={{ border: '2px solid #10b981', borderRadius: '8px', padding: '20px', textAlign: 'center', marginBottom: '16px', backgroundColor: '#f0fdf4' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '20px' }}>✅</span>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#059669' }}>Receipt Uploaded</h3>
              </div>
              
              {/* Receipt Preview */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: 'inline-block',
                  border: '2px solid #d1fae5',
                  borderRadius: '12px',
                  padding: '12px',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                  {((receipt && receipt.type?.startsWith('image/')) || (!receipt && existingReceiptIsImage && receiptPreview)) ? (
                    <img
                      src={receiptPreview || ''}
                      alt="Receipt preview"
                      style={{
                        maxWidth: '250px',
                        maxHeight: '250px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '250px',
                      height: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#374151',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
                      <div style={{ fontWeight: '500', textAlign: 'center', wordBreak: 'break-word', padding: '0 8px' }}>
                        {receipt?.name || (isEditMode && editingExpense?.receipt) || 'Receipt'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Replace/Remove Buttons */}
              <div className="submit-expense-upload-actions">
                <label className="submit-expense-upload-actions__item" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  🔄 Replace Receipt
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      if (file && !validateFileSize(file)) {
                        e.target.value = ''; // Reset input
                        return;
                      }
                      setReceipt(file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setReceiptPreview(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setReceiptPreview(null);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setReceipt(null);
                    setReceiptPreview(null);
                    setExistingReceiptIsImage(false);
                    setFileSizeWarning(''); // Clear file size warning
                    const fileInput = document.getElementById('receipt') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Remove Receipt
                </button>
              </div>
              
              {/* File Size Warning */}
              {fileSizeWarning && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#991b1b',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {fileSizeWarning}
                </div>
              )}
            </div>
          )}

          {/* No Receipt Declaration for Partners/Super Admins */}
          {(() => {
            const canDeclareNoReceipt = (userRole === 'admin' || userRole === 'partner' || userRole === 'super_admin');
            console.log('🔍 No-receipt declaration check:', { userRole, canDeclareNoReceipt });
            return canDeclareNoReceipt;
          })() && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '6px', 
              border: '1px solid #e2e8f0', 
              marginTop: '16px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px', 
                fontSize: '14px',
                color: '#374151',
                cursor: 'pointer',
                margin: 0
              }}>
                <input
                  type="checkbox"
                  checked={noReceiptDeclaration}
                  onChange={(e) => {
                    setNoReceiptDeclaration(e.target.checked);
                    // Clear VAT amount when checking 'no receipt'
                    if (e.target.checked) {
                      setVatAmount('');
                    }
                  }}
                  disabled={!!(receipt || (isEditMode && editingExpense?.receipt))}
                  style={{
                    width: '16px',
                    height: '16px',
                    margin: '2px 0 0 0',
                    cursor: (receipt || (isEditMode && editingExpense?.receipt)) ? 'not-allowed' : 'pointer',
                    accentColor: '#3b82f6',
                    outline: 'none',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    opacity: (receipt || (isEditMode && editingExpense?.receipt)) ? 0.5 : 1
                  }}
                />
                <span style={{ 
                  lineHeight: '1.5',
                  opacity: (receipt || (isEditMode && editingExpense?.receipt)) ? 0.5 : 1
                }}>
                  🙏 I declare on my honor that I don't have a receipt for this expense and that this expense relates strictly to corporate spending
                  {(receipt || (isEditMode && editingExpense?.receipt)) && (
                    <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      (Disabled because a receipt has been uploaded)
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}
          
          {/* Receipt requirement info for regular users */}
          {userRole !== 'partner' && userRole !== 'super_admin' && (
            <div style={{ padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
              <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '500' }}>
                🧾 Receipt upload is required for all expense submissions
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="submit-expense-actions">
          <button
            onClick={() => isEditMode ? navigate('/my-expenses') : window.history.back()}
            style={{ flex: 1, padding: '12px', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={isSubmitting}
            style={{ 
              flex: 1, 
              padding: '12px', 
              backgroundColor: '#ff9800', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '16px', 
              cursor: 'pointer',
              opacity: isSubmitting ? 0.5 : 1
            }}
          >
            💾 Save as Draft
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={isSubmitting || !canSubmit}
            style={{ 
              flex: 1, 
              padding: '12px', 
              backgroundColor: canSubmit ? '#10b981' : '#9ca3af', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '16px', 
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: (isSubmitting || !canSubmit) ? 0.5 : 1
            }}
          >
            ✅ Submit Expense
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            marginTop: '16px',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '500',
            backgroundColor: message.includes('success') || message.includes('draft') || message.includes('✅') || message.includes('🎉') ? '#d1fae5' : '#fee2e2',
            color: message.includes('success') || message.includes('draft') || message.includes('✅') || message.includes('🎉') ? '#065f46' : '#991b1b',
            border: `2px solid ${message.includes('success') || message.includes('draft') || message.includes('✅') || message.includes('🎉') ? '#10b981' : '#ef4444'}`,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            {message}
          </div>
        )}

        {/* FX Override Modal */}
        {showFxOverride && fetchedRate && (
          <FxOverrideModal
            fetchedRate={fetchedRate}
            currentRate={exchangeRate || 0}
            originalAmount={parseFloat(amount) || 0}
            originalCurrency={currency}
            reimbursementCurrency={userReimbursementCurrency}
            onOverrideApplied={handleFxOverrideApplied}
            onClose={() => setShowFxOverride(false)}
          />
        )}
      </div>
      </div>
    </div>
  );
};

export default SubmitExpense;

